import qs from "qs";
import swal from "sweetalert2";
import React, { useEffect, useState } from "react";
import CS from "../../../services/commonService";
import BreadCrumbs from "../../common/breadcrumbs";
import Modal from "../../common/Modal";
import SearchBox from "../../common/searchBox";
import AddUpdate from "./addUpdate";
import {
	useGetFaqCategories,
	useUpdateFaqCategory,
	useDeleteFaqCategory,
} from "../../../queryHooks";
import SingleStatusToggle from "../../common/singleStatusToggle";
import Icon from "../../icons/index";
import ReactTooltip from "react-tooltip";
import { getCurrentTimezoneDate } from "../../../util/columnsOrdering";
import Table from "../../common/table";
import { ROLES_CONFIG } from "../../../config/configs";
import { useStore } from "../../../zustand/store";
import { pageToLoadOnDelete } from "../../../helpers";
import { useLocation, useNavigate } from "react-router";
import { URL } from "../../../constants/URL";
//	Pre-defined values
const columnKey = "faqCategoriesListing";
//	Component starts
const FAQCategories = () => {
	const location = useLocation();
	const navigate = useNavigate();
	//	State values
	const setLoading = useStore((state) => state.setLoading);
	const [topicFor, setTopicFor] = useState(ROLES_CONFIG.RESTAURANT);
	const [tabChangeCounter, setTabChangeCounter] = useState(0);
	const [category, setCategory] = useState({});
	const [open, setOpen] = useState(false);
	const [tableData, setTableData] = useState();
	const [pageNow, setPageNow] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [faqCategoryPayload, setFaqCategoryPayload] = useState({
		pagination: true,
		items_per_page: itemsPerPage,
		page: pageNow,
		filter: { ["categories.role_id"]: ROLES_CONFIG.RESTAURANT },
	});

	useEffect(() => {
		switch (location.pathname) {
			case URL.faqCategoryListingUser:
				setTopicFor(ROLES_CONFIG.USER);
				break;
			case URL.faqCategoryListingRestaurant:
				setTopicFor(ROLES_CONFIG.RESTAURANT);
				break;
			default:
				setTopicFor(ROLES_CONFIG.RESTAURANT);
				break;
		}
	}, [location.pathname]);

	const {
		data: dataGetFaqCategory,
		isLoading,
		refetch,
	} = useGetFaqCategories(qs.stringify(faqCategoryPayload));

	useEffect(() => {
		if (dataGetFaqCategory && dataGetFaqCategory.status === "success") {
			let respData = dataGetFaqCategory.data;
			respData.page = pageNow;
			respData.items_per_page = itemsPerPage;
			setTableData(respData);
		}
	}, [dataGetFaqCategory]);

	const {
		mutateAsync: updateFaqCategory,
		error: errorUpdateFaqCategory,
		data: dataUpdateFaqCategory,
	} = useUpdateFaqCategory();

	useEffect(() => {
		if (dataUpdateFaqCategory) {
			setLoading(false);
			if (dataUpdateFaqCategory.status === "success") {
				CS.showSuccessMessage("Status updated successfully");
				refetch();
			} else if (dataUpdateFaqCategory.status === "fail") {
				CS.showErrorMessage(dataUpdateFaqCategory.message);
			}
		}
	}, [dataUpdateFaqCategory]);

	useEffect(() => {
		if (errorUpdateFaqCategory) {
			setLoading(false);
			CS.showErrorMessage(errorUpdateFaqCategory.response?.data?.message);
		}
	}, [errorUpdateFaqCategory]);

	const {
		mutateAsync: deleteFaqCategory,
		error: errorDeleteFaqCategory,
		data: dataDeleteFaqCategory,
	} = useDeleteFaqCategory();

	useEffect(() => {
		if (dataDeleteFaqCategory) {
			setLoading(false);
			if (dataDeleteFaqCategory.status === "success") {
				let _page = pageToLoadOnDelete(
					tableData?.total_items,
					1,
					pageNow,
					itemsPerPage
				);
				if (_page === pageNow) refetch();
				setPageNow(_page);
				setFaqCategoryPayload({ ...faqCategoryPayload, page: _page });
				CS.showSuccessMessage(dataDeleteFaqCategory.message);
			} else if (dataDeleteFaqCategory.status === "fail") {
				CS.showErrorMessage(dataDeleteFaqCategory.message);
			}
		}
	}, [dataDeleteFaqCategory]);

	useEffect(() => {
		if (errorDeleteFaqCategory) {
			setLoading(false);
			CS.showErrorMessage(errorDeleteFaqCategory.response?.data?.message);
		}
	}, [errorDeleteFaqCategory]);

	const closeModal = () => {
		setCategory({});
		setOpen(false);
		refetch();
	};

	const handleChangeTab = (tabFor) => {
		setTabChangeCounter((count) => count + 1);
		setTopicFor(tabFor);
		switch (tabFor) {
			case ROLES_CONFIG.USER:
				navigate(URL.faqCategoryListingUser);
				break;
			case ROLES_CONFIG.RESTAURANT:
				navigate(URL.faqCategoryListingRestaurant);
				break;
			default:
				navigate(URL.faqCategoryListingRestaurant);
				break;
		}
		setFaqCategoryPayload({
			pagination: true,
			items_per_page: itemsPerPage,
			page: pageNow,
			filter: { ["categories.role_id"]: tabFor },
			search: "",
		});
	};

	const columns = [
		{
			label: "Category Name",
			type: "string",
			sort: true,
			path: "name",
			alias: "categories.name",
			content: (item) => (
				<a
					href="#"
					onClick={() => {
						setOpen(true);
						setCategory(item);
					}}
				>
					{item.name}
				</a>
			),
		},
		{
			label: "Created On",
			type: "date",
			sort: true,
			path: "created_at",
			alias: "categories.created_at",
			filterDate: true,
			content: (item) => getCurrentTimezoneDate(item.created_at, true),
		},
		{
			label: "Status",
			type: "boolean",
			aliasFilter: "categories.is_active",
			filterOptions: [
				{ value: 1, label: "Active" },
				{ value: 0, label: "Inactive" },
			],
			content: (item) => (
				<SingleStatusToggle
					_id={item.id}
					status={item.is_active}
					onToggleChange={() => {
						updateFaqCategory({
							id: item.id,
							is_active: item.is_active === 0 ? 1 : 0,
						});
						setLoading(true);
					}}
					htmlFor={item.id}
				/>
			),
		},
		{
			label: "Action",
			type: "boolean",
			content: (item) => (
				<>
					<Icon
						type="edit"
						data-tip
						data-for="edit"
						onClick={() => {
							setOpen(true);
							setCategory(item);
						}}
					/>
					<ReactTooltip id="edit" place="top" effect="solid">
						Edit
					</ReactTooltip>
					<Icon
						type="delete"
						data-tip
						data-for="delete"
						onClick={() =>
							swal
								.fire({
									title: "Are you sure you want to delete?",
									icon: "warning",
									showCancelButton: true,
									confirmButtonColor: "#3085d6",
									cancelButtonColor: "#d33",
									confirmButtonText: "Yes, delete it!",
								})
								.then((result) => {
									if (result.isConfirmed) {
										deleteFaqCategory(item.id);
										setLoading(true);
									}
								})
						}
					/>
					<ReactTooltip id="delete" place="top" effect="solid">
						Delete
					</ReactTooltip>
				</>
			),
		},
	];

	return (
		<>
			<BreadCrumbs
				title="FAQs Category Management"
				data={[{ label: "FAQ Categories" }]}
			/>
			<div className="dataTables_wrapper">
				<div className="content-area position-relative">
					<div className="grid">
						<div className="left-buttons d-flex border-bottom">
							<button
								style={{ marginRight: "10px" }}
								className={
									topicFor == ROLES_CONFIG.RESTAURANT
										? "btn glow-primary btn-primary"
										: "btn btn-default minW-md-0 btn-bg-white"
								}
								onClick={() => handleChangeTab(ROLES_CONFIG.RESTAURANT)}
							>
								Restaurant
							</button>
							<button
								className={
									topicFor == ROLES_CONFIG.USER
										? "btn glow-primary btn-primary"
										: "btn btn-default minW-md-0 btn-bg-white"
								}
								onClick={() => handleChangeTab(ROLES_CONFIG.USER)}
							>
								User
							</button>
						</div>
						<div className="grid-content">
							<div className="user-listing-filterOptions mb-2 mt-3 d-block">
								<div className="row mb-2">
									<div className="col-sm-8 mb-2 position-static">
										<div className="left-buttons d-flex">
											<SearchBox
												columnKey={columnKey}
												search={(value) => {
													setPageNow(1);
													setFaqCategoryPayload({
														...faqCategoryPayload,
														search: {
															"categories.name":
																!value && value === "" ? undefined : value,
														},
														page: 1,
													});
												}}
											/>
										</div>
									</div>
									<div className="col-sm-4">
										<div className="right-buttons d-flex justify-content-end">
											<button
												onClick={() => setOpen(true)}
												id="addNewCategory"
												type="button"
												className="btn glow-primary btn-primary"
											>
												<i className="bx bx-user-plus d-lg-none" />
												<span className="d-none d-sm-none d-lg-inline-block">
													Add Category
												</span>
											</button>
										</div>
									</div>
								</div>
							</div>
							<Modal open={open} onCloseModal={closeModal}>
								<AddUpdate
									category={category}
									columnKey={columnKey}
									onCloseModal={closeModal}
									role_id={topicFor}
								/>
							</Modal>
							<Table
								columnKey={columnKey}
								columns={columns}
								tableData={tableData}
								sortCol={(obj) => {
									setPageNow(1);
									setFaqCategoryPayload({
										...faqCategoryPayload,
										sort: {
											[obj.alias]:
												!obj.sort && obj.sort === "" ? undefined : obj.sort,
										},
										page: 1,
									});
								}}
								setCurrentPage={(page) => {
									setPageNow(page);
									setFaqCategoryPayload({ ...faqCategoryPayload, page });
								}}
								setCurrentLimit={(items_per_page) => {
									setPageNow(1);
									setItemsPerPage(items_per_page);
									setFaqCategoryPayload({
										...faqCategoryPayload,
										items_per_page,
										page: 1,
									});
								}}
								loading={isLoading}
								filterValue={async (obj) => {
									if (obj.filter === null) {
										const resp = await CS.filterData(
											faqCategoryPayload.filter,
											obj
										);
										setPageNow(1);
										setFaqCategoryPayload({
											...faqCategoryPayload,
											filter: resp,
											page: 1,
										});
									} else {
										setPageNow(1);
										setFaqCategoryPayload({
											...faqCategoryPayload,
											filter: {
												...faqCategoryPayload.filter,
												[obj.alias]: obj.filter,
											},
											page: 1,
										});
									}
								}}
								filterDate={(dates) => {
									setPageNow(1);
									setFaqCategoryPayload({
										...faqCategoryPayload,
										filter_date: dates && {
											...faqCategoryPayload?.filter_date,
											[dates.alias]: String(dates.filter),
										},
										page: 1,
									});
								}}
								tabChangeCounter={tabChangeCounter}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default FAQCategories;
